import React, { useState, useEffect } from "react";
import {
  EyeIcon,
  EyeOffIcon,
  UserIcon,
  LockIcon,
  Loader2Icon,
  CheckCircleIcon,
  XCircleIcon,
  AlertCircleIcon,
  Dna,
} from "lucide-react";

interface AuthRegisterFormProps {
  onRegister: (username: string, token: string) => void;
}

export default function AuthRegisterForm({ onRegister }: AuthRegisterFormProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 检查用户名是否可用
  const checkUsername = async (username: string) => {
    if (username.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    try {
      const res = await fetch("/api/check-username", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });
      const data = await res.json();
      setUsernameAvailable(data.available);
    } catch (error) {
      setUsernameAvailable(null);
    }
  };

  // 用户名输入时检查
  useEffect(() => {
    const timer = setTimeout(() => {
      if (username) {
        checkUsername(username);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  // 验证函数
  const validateForm = () => {
    if (username.length < 3) {
      setError("用户名至少需要3个字符");
      return false;
    }
    if (username.length > 20) {
      setError("用户名不能超过20个字符");
      return false;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError("用户名只能包含字母、数字和下划线");
      return false;
    }
    if (usernameAvailable === false) {
      setError("用户名已存在，请选择其他用户名");
      return false;
    }
    if (password.length < 6) {
      setError("密码至少需要6个字符");
      return false;
    }
    if (password.length > 50) {
      setError("密码不能超过50个字符");
      return false;
    }
    if (password !== confirmPassword) {
      setError("两次输入的密码不一致");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "注册失败，请稍后重试");
      onRegister(username, data.token);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full pl-10 pr-12 py-3 rounded-xl border border-white/10 bg-white/[0.04] text-sm text-white placeholder-neutral-500 outline-none transition-all duration-200 focus:border-blue-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-blue-500/30";

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="mb-8 text-center">
        <div className="relative mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 via-blue-400 to-emerald-400 shadow-lg shadow-blue-500/30">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-emerald-400 opacity-40 blur-lg" />
          <Dna className="relative h-8 w-8 text-white" />
        </div>
        <h2 className="mb-1 text-2xl font-bold tracking-tight text-white">
          创建账户
        </h2>
        <p className="text-sm text-neutral-400">加入 BioAgent 社区</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* 用户名输入框 */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-300">用户名</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <UserIcon className="h-5 w-5 text-neutral-500" />
            </div>
            <input
              className={`${inputBase} ${
                usernameAvailable === false
                  ? "!border-red-500/60 focus:!ring-red-500/40"
                  : usernameAvailable === true
                  ? "!border-emerald-500/60 focus:!ring-emerald-500/30"
                  : ""
              }`}
              type="text"
              placeholder="3-20字符，仅字母数字下划线"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              autoComplete="username"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {usernameAvailable === true && (
                <CheckCircleIcon className="h-5 w-5 text-emerald-400" />
              )}
              {usernameAvailable === false && (
                <XCircleIcon className="h-5 w-5 text-red-500" />
              )}
            </div>
          </div>
          {usernameAvailable === false && (
            <p className="flex items-center text-sm text-red-400">
              <XCircleIcon className="mr-1 h-4 w-4" />
              用户名已存在
            </p>
          )}
          {usernameAvailable === true && (
            <p className="flex items-center text-sm text-emerald-400">
              <CheckCircleIcon className="mr-1 h-4 w-4" />
              用户名可用
            </p>
          )}
        </div>

        {/* 密码输入框 */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-300">密码</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <LockIcon className="h-5 w-5 text-neutral-500" />
            </div>
            <input
              className={inputBase}
              type={showPassword ? "text" : "password"}
              placeholder="至少6个字符"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              aria-label={showPassword ? "隐藏密码" : "显示密码"}
            >
              {showPassword ? (
                <EyeOffIcon className="h-5 w-5 text-neutral-500 transition-colors hover:text-neutral-300" />
              ) : (
                <EyeIcon className="h-5 w-5 text-neutral-500 transition-colors hover:text-neutral-300" />
              )}
            </button>
          </div>
        </div>

        {/* 确认密码输入框 */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-300">确认密码</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <LockIcon className="h-5 w-5 text-neutral-500" />
            </div>
            <input
              className={`${inputBase} pr-20 ${
                confirmPassword && password !== confirmPassword
                  ? "!border-red-500/60 focus:!ring-red-500/40"
                  : confirmPassword && password === confirmPassword
                  ? "!border-emerald-500/60 focus:!ring-emerald-500/30"
                  : ""
              }`}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="再次输入密码"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="new-password"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <button
                type="button"
                className="mr-2"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
                aria-label={showConfirmPassword ? "隐藏密码" : "显示密码"}
              >
                {showConfirmPassword ? (
                  <EyeOffIcon className="h-5 w-5 text-neutral-500 transition-colors hover:text-neutral-300" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-neutral-500 transition-colors hover:text-neutral-300" />
                )}
              </button>
              {confirmPassword && password === confirmPassword && (
                <CheckCircleIcon className="h-5 w-5 text-emerald-400" />
              )}
              {confirmPassword && password !== confirmPassword && (
                <XCircleIcon className="h-5 w-5 text-red-500" />
              )}
            </div>
          </div>
          {confirmPassword && password !== confirmPassword && (
            <p className="flex items-center text-sm text-red-400">
              <XCircleIcon className="mr-1 h-4 w-4" />
              密码不匹配
            </p>
          )}
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-3">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* 注册按钮 */}
        <button
          className="group mt-2 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 px-4 py-3 font-medium text-white shadow-lg shadow-blue-500/20 transition-all duration-200 hover:from-blue-400 hover:to-emerald-400 hover:shadow-blue-500/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-neutral-900 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          type="submit"
          disabled={loading || usernameAvailable === false}
        >
          {loading ? (
            <>
              <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
              注册中...
            </>
          ) : (
            "创建账户"
          )}
        </button>
      </form>
    </div>
  );
}
